import { useEffect, useMemo, useState } from 'react';
import {
  usePermissionCatalogue,
  useRolePermissions,
  useRoles,
  useSetRolePermissions,
} from '../api/hooks';
import { PermissionMatrix, type PermissionState } from '../components/PermissionMatrix';
import { ErrorBanner } from '../components/ui';

const SCOPE_HELP: Record<string, string> = {
  own: 'Only leads assigned to them',
  team: "Their teams' leads",
  all: 'Every lead in the organisation',
};

export function RolesPage() {
  const { data: roles = [], isPending: rolesPending } = useRoles();
  const { data: catalogue } = usePermissionCatalogue();

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const activeRoleId = selectedRoleId ?? roles[0]?.id ?? null;

  const { data: rolePermissions, isFetching } = useRolePermissions(activeRoleId ?? undefined);
  const setPermissions = useSetRolePermissions();

  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  // Reset the draft whenever a different role is loaded.
  useEffect(() => {
    if (rolePermissions) setDraft(new Set(rolePermissions.permissions));
    setSaved(false);
    setError(null);
  }, [rolePermissions]);

  const state = useMemo<Record<string, PermissionState>>(() => {
    const out: Record<string, PermissionState> = {};
    for (const p of catalogue?.data ?? []) out[p.key] = { checked: draft.has(p.key) };
    return out;
  }, [catalogue, draft]);

  const dirty = useMemo(() => {
    if (!rolePermissions) return false;
    const original = new Set(rolePermissions.permissions);
    if (original.size !== draft.size) return true;
    for (const key of draft) if (!original.has(key)) return true;
    return false;
  }, [draft, rolePermissions]);

  const editable = rolePermissions?.editable ?? false;
  const activeRole = roles.find((r) => r.id === activeRoleId);

  async function save() {
    if (!activeRoleId) return;
    setError(null);
    try {
      await setPermissions.mutateAsync({ roleId: activeRoleId, permissions: [...draft] });
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Roles &amp; permissions</h1>
          <p className="sub">
            Change what a role can do and everyone holding it is affected. For one-off exceptions,
            edit permissions on the person instead — Users → Permissions.
          </p>
        </div>
        <div className="page-head-actions">
          {isFetching && <span className="spinner" />}
          {dirty && editable && (
            <>
              <button
                className="btn"
                onClick={() => rolePermissions && setDraft(new Set(rolePermissions.permissions))}
              >
                Discard
              </button>
              <button className="btn btn-primary" onClick={save} disabled={setPermissions.isPending}>
                {setPermissions.isPending ? <span className="spinner" /> : 'Save changes'}
              </button>
            </>
          )}
        </div>
      </div>

      <ErrorBanner error={error} />
      {saved && !dirty && (
        <div className="banner banner-info">
          <div>
            Saved. Holders of this role pick up the change when their session refreshes — within 15
            minutes, or immediately if they sign out and back in.
          </div>
        </div>
      )}

      <div className="detail-grid">
        <div className="card">
          <div className="card-head">
            <h2>{activeRole ? activeRole.name : 'Permissions'}</h2>
            {activeRole && (
              <div className="card-actions">
                <span className="chip chip-plain">{draft.size} permissions</span>
              </div>
            )}
          </div>
          <div className="card-body">
            {!editable && rolePermissions && (
              <div className="banner banner-info">
                <div>
                  <strong>Super Admin cannot be edited.</strong> It is the way back in if another
                  role is misconfigured, so it always keeps every permission.
                </div>
              </div>
            )}
            {catalogue ? (
              <PermissionMatrix
                groups={catalogue.groups}
                state={state}
                disabled={!editable || setPermissions.isPending}
                onToggle={(key, next) =>
                  setDraft((d) => {
                    const copy = new Set(d);
                    if (next) copy.add(key);
                    else copy.delete(key);
                    return copy;
                  })
                }
              />
            ) : (
              <div className="skeleton" style={{ height: 300 }} />
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h2>Roles</h2></div>
          <div className="card-body" style={{ padding: 8 }}>
            {rolesPending && <div className="skeleton" style={{ height: 120 }} />}
            {roles.map((role) => {
              const isActive = role.id === activeRoleId;
              return (
                <button
                  key={role.id}
                  onClick={() => setSelectedRoleId(role.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    marginBottom: 2,
                    border: '1px solid',
                    borderColor: isActive ? 'var(--accent)' : 'transparent',
                    background: isActive ? 'var(--accent-bg)' : 'transparent',
                    borderRadius: 'var(--r)',
                    cursor: 'pointer',
                    font: 'inherit',
                    color: 'var(--ink)',
                  }}
                >
                  <div style={{ fontWeight: 650, fontSize: 13.5 }}>
                    {role.name}
                    {role.key === 'super_admin' && (
                      <span className="tag" style={{ marginLeft: 6 }}>locked</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {SCOPE_HELP[role.data_scope] ?? role.data_scope}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 3 }}>
                    {role.permission_count} permissions · {role.user_count}{' '}
                    {role.user_count === 1 ? 'user' : 'users'}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
